// 统一 API 客户端
// - 自动注入 Authorization: Bearer <token>
// - 自动 JSON 序列化 / 解析
// - 401 时自动清除本地 token 并触发跳转登录
// - 错误统一抛 ApiError，方便上层 catch

const API_BASE = '/api'; // 通过 vite 代理转发到后端 3001

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: any,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const TOKEN_KEY = 'craftforge_token';

/** 获取/设置/清除 token */
export const tokenStorage = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

/** 401 时的全局回调（authStore 注册进来，避免循环依赖） */
let on401Handler: (() => void) | null = null;
export function registerOn401(handler: () => void): void {
  on401Handler = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** 该次请求是否需要鉴权（默认 true，登录/bootstrap 接口设为 false） */
  auth?: boolean;
  /** 自定义额外 headers */
  headers?: Record<string, string>;
}

/** 通用请求函数 */
export async function apiFetch<T = any>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, headers = {} } = opts;
  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };
  let token: string | null = null;
  if (auth) {
    token = tokenStorage.get();
    if (token) reqHeaders['Authorization'] = `Bearer ${token}`;
  }
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers: reqHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // 401 处理：清除 token + 通知全局
  // ⚠️ 游客模式（无 token）调任何鉴权 API 都会得到 401，但**不应触发 forceLogout**
  //    游客的 user.id === 'guest'，没有真实 token。若被 forceLogout 会被踢回登录页。
  if (resp.status === 401 && auth) {
    // 仅当当前持有 token（即真实登录用户）时才走 forceLogout 流程
    if (token) {
      tokenStorage.clear();
      if (on401Handler) on401Handler();
    }
  }

  let respBody: any = null;
  const text = await resp.text();
  try {
    respBody = text ? JSON.parse(text) : null;
  } catch {
    respBody = text;
  }

  if (!resp.ok) {
    const msg =
      (respBody && (respBody.error || respBody.message)) ||
      `HTTP ${resp.status} ${resp.statusText}`;
    throw new ApiError(resp.status, respBody, msg);
  }
  return respBody as T;
}

// =============================================================
// Auth API
// =============================================================
export interface PublicUser {
  id: string;
  username: string;
  display_name: string;
  student_no: string | null;
  role: 'student' | 'teacher' | 'admin';
  class_id: string | null;
  must_change_pw: boolean;
  created_at: number;
  last_login_at: number | null;
}

export const authApi = {
  login: (username: string, password: string) =>
    apiFetch<{ token: string; user: PublicUser }>('/auth/login', {
      method: 'POST',
      body: { username, password },
      auth: false,
    }),
  me: () => apiFetch<{ user: PublicUser }>('/auth/me'),
  logout: () => apiFetch<{ ok: true }>('/auth/logout', { method: 'POST' }),
  changePassword: (oldPw: string, newPw: string) =>
    apiFetch<{ ok: true }>('/auth/change-password', {
      method: 'POST',
      body: { old_password: oldPw, new_password: newPw },
    }),
  bootstrapTeacher: (data: {
    username: string;
    password: string;
    display_name: string;
    student_no?: string;
  }) => apiFetch<{ user: PublicUser }>('/auth/bootstrap-teacher', {
    method: 'POST',
    body: data,
    auth: false,
  }),
  /** 管理员创建账号 */
  register: (data: {
    username: string;
    password: string;
    display_name: string;
    student_no?: string;
    role: 'student' | 'teacher' | 'admin';
    class_id?: string;
  }) => apiFetch<{ user: PublicUser }>('/auth/register', { method: 'POST', body: data }),
};

// =============================================================
// 班级 API
// =============================================================
export interface ClassRow {
  id: string;
  name: string;
  teacher_id: string;
  join_code: string;
  created_at: number;
}

export const classApi = {
  create: (name: string) =>
    apiFetch<{ class: ClassRow }>('/classes', { method: 'POST', body: { name } }),
  mine: () => apiFetch<{ classes: ClassRow[] }>('/classes/mine'),
  detail: (id: string) =>
    apiFetch<{ class: ClassRow; member_count: number }>(`/classes/${id}`),
  members: (id: string) =>
    apiFetch<{ members: PublicUser[] }>(`/classes/${id}/members`),
  /** 申请加入班级（审批制）返回 status: 'pending' | 'already_member' */
  join: (joinCode: string) =>
    apiFetch<{ status: string; class: ClassRow; request_id?: string }>('/classes/join', {
      method: 'POST',
      body: { join_code: joinCode.toUpperCase() },
    }),
  /** 学生查我的最新申请状态 */
  myRequest: () =>
    apiFetch<{ request: null | { id: string; class_id: string; status: string; created_at: number; class_name: string; join_code: string } }>('/classes/my-request'),
  /** 教师查待审批列表 */
  pendingRequests: (classId: string, status: 'pending' | 'approved' | 'rejected' = 'pending') =>
    apiFetch<{ requests: Array<{ request_id: string; status: string; created_at: number; user_id: string; username: string; display_name: string; student_no: string | null }> }>(
      `/classes/${classId}/requests?status=${status}`,
    ),
  /** 教师批准 / 拒绝 */
  reviewRequest: (reqId: string, action: 'approve' | 'reject') =>
    apiFetch<{ ok: true; status: string }>(`/classes/requests/${reqId}/review`, {
      method: 'POST',
      body: { action },
    }),
  /** 把学生踢出班级 */
  kick: (classId: string, userId: string) =>
    apiFetch<{ ok: true }>(`/classes/${classId}/kick/${userId}`, { method: 'POST' }),
  regenCode: (id: string) =>
    apiFetch<{ join_code: string }>(`/classes/${id}/regen-code`, { method: 'POST' }),
  remove: (id: string) =>
    apiFetch<{ ok: true }>(`/classes/${id}`, { method: 'DELETE' }),
};

// =============================================================
// 演练记录 API
// =============================================================
export interface DrillRecord {
  id: string;
  user_id: string;
  scene_id: string;
  fault_id: string;
  fault_name: string;
  start_time: number;
  end_time: number;
  duration_sec: number;
  score: number;
  grade: 'S' | 'A' | 'B' | 'C' | 'D';
  difficulty: 'novice' | 'standard' | 'expert' | null;
  score_breakdown: any;
  operations: any;
  created_at: number;
}

export const drillApi = {
  submit: (data: {
    scene_id: string;
    fault_id: string;
    fault_name: string;
    start_time: number;
    end_time: number;
    duration_sec: number;
    score: number;
    grade: 'S' | 'A' | 'B' | 'C' | 'D';
    difficulty?: string | null;
    score_breakdown?: any;
    operations?: any;
  }) =>
    apiFetch<{ id: string; new_achievements: string[] }>('/drill-records', {
      method: 'POST',
      body: data,
    }),
  list: (params: { scene_id?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.scene_id) qs.set('scene_id', params.scene_id);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    return apiFetch<{ records: DrillRecord[]; total: number; limit: number; offset: number }>(
      `/drill-records${qs.toString() ? '?' + qs.toString() : ''}`,
    );
  },
  detail: (id: string) => apiFetch<{ record: DrillRecord }>(`/drill-records/${id}`),
  mySummary: () =>
    apiFetch<{
      total: number;
      avg_score: number;
      best_score: number;
      grade_count: Record<string, number>;
      scene_stats: Record<string, { count: number; avg: number; best: number }>;
      recent_curve: Array<{ created_at: number; score: number; grade: string; scene_id: string }>;
    }>('/drill-records/me/summary'),
};

// =============================================================
// 成就 API
// =============================================================
export interface Achievement {
  key: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlocked_at: number | null;
}

export const achievementApi = {
  list: () => apiFetch<{ achievements: Achievement[] }>('/achievements'),
};

// =============================================================
// 教师统计 API
// =============================================================
export interface ClassDashboard {
  class_id: string;
  class_name: string;
  join_code: string;
  student_count: number;
  active_student_count_7d: number;
  drill_total: number;
  avg_score: number;
  covered_scenes: string[];
}

export const teacherApi = {
  dashboard: () => apiFetch<{ dashboard: ClassDashboard[] }>('/teacher/dashboard'),
  studentDetail: (id: string) =>
    apiFetch<{
      student: PublicUser;
      total_drills: number;
      avg_score: number;
      recent_records: DrillRecord[];
    }>(`/teacher/student/${id}`),
};

// =============================================================
// 错题本 API
// =============================================================
export interface Mistake {
  id: string; user_id: string; scene_id: string;
  fault_id: string; fault_name: string;
  fail_count: number; last_fail_at: number;
  last_score: number; last_grade: string;
  status: 'open' | 'mastered'; mastered_at: number | null;
  created_at: number;
  // 详细信息（后端关联查询）
  latest_record_id?: string;
  latest_difficulty?: string | null;
  latest_duration?: number;
  latest_created_at?: number;
  coach_comment?: string;
  error_operations?: Array<{ action?: string; isCorrect: boolean; timestamp?: number; parameterChange?: { param: string; from: number; to: number } }>;
  dimensions?: Array<{ key: string; label: string; score: number; max: number; explain: string; suggestion?: string }>;
  highlights?: string[];
  improvements?: string[];
  score_history?: Array<{ score: number; grade: string; created_at: number; duration_sec: number }>;
  top_error_patterns?: Array<{ action: string; count: number }>;
  total_attempts?: number;
}

export const mistakeApi = {
  list: (status: 'open' | 'mastered' | 'all' = 'open') =>
    apiFetch<{ mistakes: Mistake[]; stats: Record<string, number> }>(
      `/mistakes${status === 'open' ? '' : '?status=' + status}`,
    ),
  master: (id: string) =>
    apiFetch<{ ok: true }>(`/mistakes/${id}/master`, { method: 'POST' }),
  add: (data: { scene_id: string; fault_id: string; fault_name: string; score?: number; grade?: 'S' | 'A' | 'B' | 'C' | 'D' }) =>
    apiFetch<{ ok: true; action: string; id: string }>('/mistakes', { method: 'POST', body: data }),
};

// =============================================================
// 排行榜 API
// =============================================================
export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  class_id: string | null;
  class_name: string | null;
  total_drills: number;
  avg_score: number;
  s_count: number;
}

export const leaderboardApi = {
  query: (params: {
    scope?: 'class' | 'global';
    window?: '30d' | 'all';
    metric?: 'avg' | 'drill_count' | 's_count';
  }) => {
    const qs = new URLSearchParams();
    qs.set('scope',  params.scope  ?? 'class');
    qs.set('window', params.window ?? '30d');
    qs.set('metric', params.metric ?? 'avg');
    return apiFetch<{
      leaderboard: LeaderboardEntry[];
      my_rank: number | null;
      my_extra_row: LeaderboardEntry | null;
      total: number;
      scope: string;
      window: string;
      metric: string;
      note?: string;
    }>(`/leaderboard?${qs.toString()}`);
  },
};

// =============================================================
// 学习数据分析 (Learning Analytics)
// =============================================================

export interface WeaknessResponse {
  total_drills: number;
  avg_score: number;
  score_trend: 'improving' | 'declining' | 'stable';
  scene_weakness: SceneWeakness[];
  fault_difficulty: FaultDifficulty[];
  parameter_errors: ParameterError[];
  dimension_scores: Record<string, { avg: number; max: number; trend: string }>;
  time_analysis: { avg_duration: number; fastest: number; slowest: number; trend: string };
  recommendations: Recommendation[];
}

export interface SceneWeakness {
  scene_id: string;
  scene_name: string;
  drill_count: number;
  avg_score: number;
  best_score: number;
  avg_duration: number;
  weakness_tags: string[];
  score_trend: string;
  last_drill_at: number;
}

export interface FaultDifficulty {
  fault_id: string;
  fault_name: string;
  scene_id: string;
  scene_name: string;
  attempt_count: number;
  avg_score: number;
  best_grade: string;
  avg_duration: number;
  common_errors: string[];
  is_mastered: boolean;
}

export interface ParameterError {
  param_id: string;
  param_name: string;
  scene_id: string;
  scene_name: string;
  error_count: number;
  avg_deviation: number;
  direction: 'high' | 'low';
  typical_value: number;
}

export interface Recommendation {
  type: string;
  priority: 'high' | 'medium' | 'low';
  scene_id?: string;
  scene_name?: string;
  fault_id?: string;
  fault_name?: string;
  reason: string;
  suggested_action: string;
}

export interface RadarResponse {
  radar: Array<{ dimension: string; score: number; max: number }>;
}

export interface ClassAnalyticsResponse {
  total_drills: number;
  avg_score: number;
  student_count: number;
  scene_weakness: Array<{ scene_id: string; scene_name: string; drill_count: number; avg_score: number; student_coverage: number }>;
  at_risk_students: Array<{ user_id: string; name: string; avg_score: number; drill_count: number; trend: string; risk_level: string; reasons: string[]; last_drill_at: number }>;
  top_students: Array<{ user_id: string; name: string; avg_score: number; drill_count: number; s_count: number }>;
  // 深化维度
  grade_distribution?: Record<string, number>;
  activity_trend?: Array<{ date: string; drill_count: number; avg_score: number }>;
  class_dimensions?: Array<{ key: string; label: string; avg: number; max: number; rate: number }>;
  fault_hotspots?: Array<{ fault_id: string; fault_name: string; scene_id: string; scene_name: string; fail_count: number; total_count: number; fail_rate: number; open_mistakes: number }>;
  all_students?: Array<{ user_id: string; name: string; avg_score: number; best_score: number; drill_count: number; s_count: number; trend: string; last_drill_at: number }>;
}

export interface AIInsightResponse {
  insight: string;
}

export const analyticsApi = {
  myWeakness: () => apiFetch<WeaknessResponse>('/analytics/me/weakness'),
  myRadar: () => apiFetch<RadarResponse>('/analytics/me/radar'),
  classOverview: (classId: string) => apiFetch<ClassAnalyticsResponse>(`/analytics/teacher/class/${classId}/overview`),
  aiInsight: (classId: string) => apiFetch<AIInsightResponse>(`/analytics/teacher/class/${classId}/ai-insight`, { method: 'POST' }),
};

// =============================================================
// 专家经验蒸馏 (Experience Distillation)
// =============================================================

export interface DistilledExperience {
  key_decisions: Array<{ step: number; timing: string; action: string; reasoning: string; priority: 'critical' | 'recommended' }>;
  param_thresholds: Array<{ param: string; danger_zone: string; expert_action: string; normal_range: string }>;
  rhythm: string;
  intuition_rules: string[];
  common_mistakes: Array<{ mistake: string; why_wrong: string; correct_alternative: string }>;
  master_insight: string;
}

export interface ExperienceRule {
  id: string;
  scene_id: string;
  fault_id: string | null;
  fault_name: string;
  title: string;
  raw_transcript?: string;
  raw_annotations?: string | null;
  distilled: DistilledExperience | null;
  /** P1-4: 专家时间轴（GET 详情 / GET by-fault 才会返回） */
  timeline?: import('./timeline').ExpertTimeline | null;
  /** P1-4: 时间轴来源演练记录 id（可空） */
  source_record_id?: string | null;
  expert_name: string | null;
  expert_title: string | null;
  source_type: string;
  status: string;
  created_at: number;
  updated_at: number;
  distill_error?: string | null;
}

export interface CollectResponse {
  id: string;
  distilled: DistilledExperience | null;
  distill_error: string | null;
  message: string;
}

/** P1-4: 从演练记录提升为专家时间轴的预览返回 */
export interface FromRecordPreview {
  record: {
    id: string;
    scene_id: string;
    fault_id: string;
    fault_name: string;
    score: number;
    grade: string;
    duration_sec: number;
    created_at: number;
  };
  timeline: import('./timeline').ExpertTimeline;
  suggested_title: string;
  suggested_transcript: string;
}

/** P1-4: 教师可见的高分演练池条目（含学员姓名，用于"从演练记录提升"下拉） */
export interface HighScoreRecord {
  id: string;
  user_id: string;
  scene_id: string;
  fault_id: string;
  fault_name: string;
  grade: 'S' | 'A';
  score: number;
  duration_sec: number;
  created_at: number;
  username: string | null;
  user_name: string | null;
}

export const experienceApi = {
  collect: (data: {
    scene_id: string; fault_name: string; title: string; raw_transcript: string;
    fault_id?: string; raw_annotations?: string; expert_name?: string; expert_title?: string; source_type?: string;
    /** P1-4: 可选时间轴 */
    timeline?: import('./timeline').ExpertTimeline | null;
    /** P1-4: 时间轴来源演练记录 id */
    source_record_id?: string;
  }) => apiFetch<CollectResponse>('/experience/collect', { method: 'POST', body: JSON.stringify(data) }),
  redistill: (id: string) => apiFetch<{ id: string; distilled: DistilledExperience; message: string }>(`/experience/${id}/redistill`, { method: 'POST' }),
  list: (params?: { scene_id?: string; fault_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.scene_id) qs.set('scene_id', params.scene_id);
    if (params?.fault_id) qs.set('fault_id', params.fault_id);
    const q = qs.toString();
    return apiFetch<{ experiences: ExperienceRule[] }>(`/experience${q ? '?' + q : ''}`);
  },
  detail: (id: string) => apiFetch<ExperienceRule>(`/experience/${id}`),
  update: (id: string, data: Partial<ExperienceRule>) => apiFetch<{ message: string; id: string }>(`/experience/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) => apiFetch<{ message: string; id: string }>(`/experience/${id}`, { method: 'DELETE' }),
  getByScene: (sceneId: string) => apiFetch<{ experiences: ExperienceRule[] }>(`/experience/scene/${sceneId}`),
  getByFault: (sceneId: string, faultId: string) => apiFetch<{ experience: ExperienceRule | null }>(`/experience/fault/${sceneId}/${faultId}`),
  /** P1-4: 教师把某次演练记录预览为专家时间轴草稿（不落库） */
  fromRecordPreview: (recordId: string) => apiFetch<FromRecordPreview>(`/experience/from-record/${recordId}`),
  /** P1-4: 教师/管理员可见的全班高分演练池（S/A），用于"从演练记录提升"下拉 */
  highScorePool: (params?: { scene_id?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.scene_id) qs.set('scene_id', params.scene_id);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return apiFetch<{ records: HighScoreRecord[] }>(`/experience/high-score-pool${q ? '?' + q : ''}`);
  },
  /** P1-4: 学生侧 — 取自己演练的时间轴（用于对照专家） */
  studentTimeline: (recordId: string) =>
    apiFetch<{ record_id: string; scene_id: string; fault_id: string; timeline: import('./timeline').ExpertTimeline }>(
      `/experience/student-timeline/${recordId}`,
    ),
};
